import type { Label } from '@seen/core/models/label'
import type { ReactElement, ReactNode } from 'react'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'

interface DeletedTooltipProps {
  isDeleted: boolean
  children: ReactElement
}

/*
 * Wraps content with a "Deleted" tooltip when isDeleted is true.
 * Children must be a single React element (for asChild to work).
 */
export function DeletedTooltip({
  isDeleted,
  children,
}: DeletedTooltipProps): ReactNode {
  if (!isDeleted) {
    return children
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>Deleted</TooltipContent>
    </Tooltip>
  )
}

interface DeletedLabelIndicatorProps {
  label: Label
}

/*
 * Displays a label with colored dot and name.
 * If the label is deleted, shows dimmed styling with a "Deleted" tooltip.
 */
export function DeletedLabelIndicator({
  label,
}: DeletedLabelIndicatorProps): ReactNode {
  return (
    <DeletedTooltip isDeleted={label.isDeleted}>
      <span className="inline-flex items-center gap-0.5">
        <LabelColor
          color={label.color}
          className={label.isDeleted ? 'opacity-50' : undefined}
        />
        <span
          className={
            label.isDeleted
              ? 'font-medium text-foreground opacity-50'
              : 'font-medium text-foreground'
          }
        >
          {label.name}
        </span>
      </span>
    </DeletedTooltip>
  )
}
